<template>
  <div class="ssm-bg" aria-hidden="true">
    <canvas
      ref="base"
      class="ssm-bg__layer"
      :class="{ 'is-ready': ready }"
    />
    <canvas ref="fx" class="ssm-bg__layer" />
  </div>
</template>

<script>
import { renderBackground } from '@/pixel/background.js'
import { reducedMotion, watchReducedMotion, whenIdle } from '@/motion.js'

// The warped block checkerboard from the poster, fixed behind every page.
// Every few seconds a couple of scanlines tear sideways.
const PX = 3

export default {
    data () {
        return { ready: false }
    },
    mounted () {
        this.width = 0
        this.height = 0
        whenIdle(() => {
            this.render()
            this.scheduleBurst()
        })
        this.onResize = () => {
            clearTimeout(this.resizeTimer)
            this.resizeTimer = setTimeout(this.render, 300)
        }
        window.addEventListener('resize', this.onResize)
        this.unwatchMotion = watchReducedMotion(this.scheduleBurst)
    },
    beforeUnmount () {
        window.removeEventListener('resize', this.onResize)
        this.unwatchMotion()
        clearTimeout(this.resizeTimer)
        clearTimeout(this.burstTimer)
        clearTimeout(this.frameTimer)
    },
    methods: {
        render () {
            const w = Math.ceil(window.innerWidth / PX)
            // Cover the tallest the viewport gets, so mobile toolbars don't cause re-renders.
            const h = Math.ceil(Math.max(window.innerHeight, screen.height) / PX)
            if (w === this.width && h <= this.height) return
            this.width = w
            this.height = Math.max(h, this.height)

            const image = renderBackground(this.width, this.height)
            for (const c of [this.$refs.base, this.$refs.fx]) {
                if (!c) continue
                c.width = this.width
                c.height = this.height
                c.style.width = `${this.width * PX}px`
                c.style.height = `${this.height * PX}px`
            }
            this.$refs.base.getContext('2d').putImageData(new ImageData(image.d, this.width, this.height), 0, 0)
            this.ready = true
        },
        scheduleBurst () {
            clearTimeout(this.burstTimer)
            if (reducedMotion()) return
            this.burstTimer = setTimeout(this.burst, 4000 + Math.random() * 7000)
        },
        burst () {
            const ctx = this.$refs.fx && this.$refs.fx.getContext('2d')
            if (!ctx || !this.ready || document.hidden) return this.scheduleBurst()
            const { width, height } = this
            let frames = 2 + Math.floor(Math.random() * 4)
            const step = () => {
                ctx.clearRect(0, 0, width, height)
                if (frames-- <= 0) return this.scheduleBurst()
                for (let i = 1 + Math.floor(Math.random() * 3); i > 0; i--) {
                    const y = Math.floor(Math.random() * height)
                    const h = 1 + Math.floor(Math.random() * 6)
                    const dx = Math.round((Math.random() - 0.5) * 24)
                    ctx.drawImage(this.$refs.base, 0, y, width, h, dx, y, width, h)
                }
                this.frameTimer = setTimeout(step, 50 + Math.random() * 60)
            }
            step()
        },
    },
}
</script>

<style scoped>
.ssm-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 30% 10%, #3b1d6e 0%, transparent 60%),
    linear-gradient(#1f1238, #0e0a1a);
}

.ssm-bg__layer {
  position: absolute;
  top: 0;
  left: 0;
  image-rendering: pixelated;
}

.ssm-bg__layer:first-child {
  opacity: 0;
  transition: opacity 0.6s steps(4, end);
}

.ssm-bg__layer.is-ready {
  opacity: 1;
}
</style>
